// Content Slots panel — auto-detects <!-- slot:name --> pairs in template HTML
// and lets users configure each slot's type and layout.

import { ChevronDown, ChevronRight, Code, Copy } from 'lucide-react'
import { useMemo, useState } from 'react'

const SLOT_TYPES = [
  { value: 'article', label: 'Article' },
  { value: 'ad', label: 'Ad' },
  { value: 'html', label: 'Raw HTML' },
] as const

const SLOT_LAYOUTS = [
  { value: 'featured', label: 'Featured' },
  { value: 'card', label: 'Card' },
  { value: 'compact', label: 'Compact' },
  { value: 'raw', label: 'No layout (raw)' },
] as const

export interface SlotConfig {
  name: string
  type: 'article' | 'ad' | 'html'
  layout: 'featured' | 'card' | 'compact' | 'raw'
  required: boolean
}

interface SlotPanelProps {
  html: string
  slots: SlotConfig[]
  onSlotsChange: (slots: SlotConfig[]) => void
}

function detectSlotNames(html: string): string[] {
  const names = new Set<string>()
  const re = /<!--\s*slot:(\w+)\s*-->/g
  let match: RegExpExecArray | null = re.exec(html)
  while (match) {
    if (match[1]) names.add(match[1])
    match = re.exec(html)
  }
  return Array.from(names)
}

export function TemplateSlotPanel({ html, slots, onSlotsChange }: SlotPanelProps): React.JSX.Element {
  const [open, setOpen] = useState(false)

  const detectedNames = useMemo(() => detectSlotNames(html), [html])

  // Sync: add newly detected, keep config for existing, drop removed
  const syncedSlots = useMemo(() => {
    return detectedNames.map((name) => {
      const existing = slots.find((s) => s.name === name)
      if (existing) return existing
      return { name, type: 'article' as const, layout: 'card' as const, required: true }
    })
  }, [detectedNames, slots])

  // Push synced slots up if they changed
  const slotsKey = syncedSlots.map((s) => s.name).join(',')
  const propsKey = slots.map((s) => s.name).join(',')
  if (slotsKey !== propsKey) {
    // Schedule update to avoid render-during-render
    setTimeout(() => onSlotsChange(syncedSlots), 0)
  }

  function updateSlot(name: string, patch: Partial<SlotConfig>): void {
    onSlotsChange(syncedSlots.map((s) => (s.name === name ? { ...s, ...patch } : s)))
  }

  const count = detectedNames.length

  return (
    <div className="mt-4 rounded-xl border border-surface-3 bg-surface-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-surface-1"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary">Content Slots</span>
          {count > 0 && (
            <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
              {count}
            </span>
          )}
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-text-tertiary" />
        ) : (
          <ChevronRight className="h-4 w-4 text-text-tertiary" />
        )}
      </button>

      {open && (
        <div className="border-t border-surface-3 p-4">
          {count === 0 ? (
            <EmptySlotGuide />
          ) : (
            <div className="space-y-3">
              {syncedSlots.map((slot) => (
                <div key={slot.name} className="rounded-lg border border-surface-3 bg-surface-1 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Code className="h-3.5 w-3.5 text-text-tertiary" />
                    <span className="font-mono text-sm font-medium text-text-primary">{slot.name}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-text-secondary">
                      Type
                      <select
                        value={slot.type}
                        onChange={(e) => updateSlot(slot.name, { type: e.target.value as SlotConfig['type'] })}
                        className="rounded border border-surface-3 bg-surface-0 px-2 py-1 text-xs text-text-primary"
                      >
                        {SLOT_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-text-secondary">
                      Layout
                      <select
                        value={slot.layout}
                        onChange={(e) => updateSlot(slot.name, { layout: e.target.value as SlotConfig['layout'] })}
                        className="rounded border border-surface-3 bg-surface-0 px-2 py-1 text-xs text-text-primary"
                      >
                        {SLOT_LAYOUTS.map((l) => (
                          <option key={l.value} value={l.value}>{l.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-text-secondary">
                      <input
                        type="checkbox"
                        checked={slot.required}
                        onChange={(e) => updateSlot(slot.name, { required: e.target.checked })}
                        className="h-3.5 w-3.5 rounded border-surface-3 text-brand-500"
                      />
                      Required
                    </label>
                  </div>
                </div>
              ))}
              <p className="text-xs text-text-tertiary">
                Slots are filled during campaign creation. Required slots must have content before sending.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function EmptySlotGuide(): React.JSX.Element {
  const [copied, setCopied] = useState(false)
  const example = '<!-- slot:featured -->\n<tr><td>Content goes here</td></tr>\n<!-- /slot:featured -->'

  function handleCopy(): void {
    navigator.clipboard.writeText(example)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        No content slots detected. Add slots to your HTML to define dynamic regions that get filled per campaign.
      </p>
      <div className="rounded-lg bg-surface-2 p-3">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-xs font-medium text-text-secondary">Example</span>
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600"
          >
            <Copy className="h-3 w-3" />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <pre className="text-xs text-text-tertiary">
{`<!-- slot:featured -->
<tr><td>This gets replaced</td></tr>
<!-- /slot:featured -->`}
        </pre>
      </div>
      <p className="text-xs text-text-tertiary">
        Works with any external editor — HTML comments survive Unlayer, MJML, and all email builders.
      </p>
    </div>
  )
}
